import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from './cn.js';

const ToastContext = createContext(null);

const ICONS = { success: CheckCircle2, error: XCircle, info: Info };
const STYLES = {
  success: 'ring-emerald-200 [&_svg]:text-emerald-600',
  error: 'ring-red-200 [&_svg]:text-red-600',
  info: 'ring-slate-200 [&_svg]:text-sky-600',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (tone, message, opts = {}) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((t) => [...t.slice(-3), { id, tone, message, title: opts.title, action: opts.action }]);
      // Toasts with an action stay longer so there's time to use it.
      setTimeout(() => dismiss(id), opts.duration ?? (opts.action ? 12000 : tone === 'error' ? 7000 : 4000));
    },
    [dismiss],
  );
  const api = useMemo(
    () => ({
      success: (m, o) => push('success', m, o),
      error: (m, o) => push('error', typeof m === 'string' ? m : (m?.message ?? 'Something went wrong'), o),
      info: (m, o) => push('info', m, o),
    }),
    [push],
  );
  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4 sm:items-end sm:pr-6"
        >
          {toasts.map((t) => {
            const Icon = ICONS[t.tone];
            return (
              <div
                key={t.id}
                className={cn(
                  'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl bg-white p-3 text-sm shadow-lg ring-1',
                  STYLES[t.tone],
                )}
              >
                <Icon className="mt-0.5 size-5 shrink-0" aria-hidden />
                <div className="min-w-0 flex-1 text-slate-800">
                  {t.title && <p className="font-semibold">{t.title}</p>}
                  <p>{t.message}</p>
                  {t.action && (
                    <button
                      type="button"
                      onClick={() => {
                        dismiss(t.id);
                        t.action.onClick();
                      }}
                      className="mt-1.5 font-semibold text-brand-700 hover:underline"
                    >
                      {t.action.label}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => dismiss(t.id)}
                  className="rounded p-0.5 text-slate-400 hover:text-slate-700"
                  aria-label="Dismiss"
                >
                  <X className="size-4" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
